import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n';
import enData from '../../data/hue-color-content-enus.json';
import ptData from '../../data/hue-color-content-ptbr.json';
import './hue-color-bubbles.scss';
import { Button } from '../button/button';

export interface HueColorEntry {
  'hue-value': number;
  'color-title': string;
  'color-text': string;
  'color-curiosidade': string;
}

interface HueColorBubblesProps {
  hue: number;
}

function angularDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

const SHOW_RANGE = 10;

interface BalloonInstance {
  hue: number;
  entry: HueColorEntry;
  side: 'left' | 'right';
  phase: 'in' | 'out';
}

const hueOf = (e: HueColorEntry) => e['hue-value'];

export function HueColorBubbles({ hue }: HueColorBubblesProps) {
  const { language } = useI18n();
  const data = language === 'pt' ? ptData : enData;

  let closest = data[0];
  let closestDist = angularDist(hue, closest['hue-value']);
  for (let i = 1; i < data.length; i++) {
    const d = angularDist(hue, data[i]['hue-value']);
    if (d < closestDist) {
      closestDist = d;
      closest = data[i];
    }
  }
  const inRange = closestDist <= SHOW_RANGE;
  const winner: HueColorEntry | null = inRange ? closest : null;

  const [items, setItems] = useState<BalloonInstance[]>([]);
  const itemsRef = useRef<BalloonInstance[]>([]);
  const sideCounterRef = useRef(0);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const invoke = (entry: HueColorEntry) => {
      const h = hueOf(entry);
      setItems((prev) => {
        if (prev.some((it) => it.hue === h && it.phase === 'in')) return prev;
        if (prev.some((it) => it.hue === h)) {
          return prev.map((p) =>
            p.hue === h ? { ...p, phase: 'in' as const } : p
          );
        }
        const side: 'left' | 'right' =
          sideCounterRef.current % 2 === 0 ? 'left' : 'right';
        sideCounterRef.current += 1;
        return [...prev, { hue: h, entry, side, phase: 'in' }];
      });
    };

    const deactivate = (h: number) => {
      setItems((prev) =>
        prev.map((p) => (p.hue === h ? { ...p, phase: 'out' as const } : p))
      );
    };

    if (!winner) {
      for (const it of itemsRef.current) deactivate(it.hue);
      return;
    }

    for (const it of itemsRef.current) {
      if (it.hue !== hueOf(winner)) deactivate(it.hue);
    }
    invoke(winner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winner]);

  const winnerInstance =
    winner != null
      ? items.find((it) => it.hue === hueOf(winner) && it.phase === 'in')
      : null;
  const shown = winnerInstance?.entry ?? null;

  return (
    <div className="hue-bubbles" aria-hidden="false">
      {items.map((it) =>
        createPortal(
          <div
            key={it.hue}
            className={`hue-bubbles__balloon hue-bubbles__balloon--${it.side} is-in ${
              it.phase === 'out' ? 'is-leaving' : ''
            }`}
          >
            <div className="hue-bubbles__content">
              <div
                className="hue-bubbles__color"
                style={{ background: colorFor(it.entry) }}
              />
              <div className="hue-bubbles__info">
                <h3 className="hue-bubbles__title">{it.entry['color-title']}</h3>
                <p className="hue-bubbles__text">{it.entry['color-text']}</p>
                <p className="hue-bubbles__curiosity">
                  {it.entry['color-curiosidade']}
                </p>
              </div>
            </div>
          </div>,
          document.body
        )
      )}

      {shown && (
        <button
          type="button"
          className="hue-bubbles__chip"
          onClick={() => setPopoverOpen(true)}
          aria-haspopup="dialog"
        >
          <span
            className="hue-bubbles__chip-color"
            style={{ background: colorFor(shown) }}
          />
          <span className="hue-bubbles__chip-label">{shown['color-title']}</span>
        </button>
      )}

      {popoverOpen && shown && (
        <div
          className="hue-bubbles__overlay"
          onClick={() => setPopoverOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="hue-bubbles__popover"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="hue-bubbles__popover-color"
              style={{ background: colorFor(shown) }}
            />
            <h3 className="hue-bubbles__popover-title">{shown['color-title']}</h3>
            <p className="hue-bubbles__popover-text">{shown['color-text']}</p>
            <p className="hue-bubbles__popover-curiosity">
              {shown['color-curiosidade']}
            </p>
            <Button
              size='md'
              type='button'
              className="hue-bubbles__popover-close"
              onClick={() => setPopoverOpen(false)}
            >
              {language === 'pt' ? 'Fechar' : 'Close'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function colorFor(entry: HueColorEntry): string {
  return `hsl(${entry['hue-value']}, 80%, 55%)`;
}
